import React, { useCallback, useState } from 'react';

import { FileUploadForm } from '../file-upload-form';
import { HtmlPastePanel } from './paste-panel';

type Props = {
  onNewFile: (file: File) => void;
  onSubmitSource: (source: string) => Promise<void> | void;
};

// Both entry points in one panel, upload first.
//
// JOU-40 requires file upload and paste-raw-HTML, and they are not equal in
// practice: someone who just built something in Claude has HTML in a chat
// window, not a file on disk. Making paste a second-class path behind a tab
// would hide the case most people arrive with. Both are visible at once, and
// the panel says what it accepts so "Upload a file" is no longer the thing
// people reach for by default.
export const AddHtmlControlPanelContent = ({ onNewFile, onSubmitSource }: Props) => {
  const [error, setError] = useState('');

  const onFile = useCallback(
    (file: File) => {
      // The picker is scoped to .html, but a drag-and-drop bypasses that
      // filter, so the check has to happen here too. The server sniffs the
      // bytes regardless — this is only for a fast, specific message.
      const looksHtml = file.type === 'text/html' || /\.html?$/i.test(file.name);
      if (!looksHtml) {
        setError('That is not an HTML file.');
        return;
      }
      setError('');
      onNewFile(file);
    },
    [onNewFile]
  );

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <FileUploadForm onNewFile={onFile} accept='text/html,.html,.htm' label='Click to upload an HTML file' />
        {error && <p className='mt-2 text-bedrock-p text-bedrock-red'>{error}</p>}
      </div>

      <div className='flex items-center gap-2'>
        <span className='h-px flex-1 bg-bedrock-light-gray' />
        <span className='text-bedrock-p text-bedrock-dark-gray'>or paste it</span>
        <span className='h-px flex-1 bg-bedrock-light-gray' />
      </div>

      <HtmlPastePanel onSubmitSource={onSubmitSource} />

      {/* We modify the uploaded file, so we say so. Adding a script to
          someone's document without telling them is not a thing to do
          quietly, even when the script only reports a height. */}
      <p className='text-bedrock-serif-small text-bedrock-dark-gray'>
        Journey adds a small script and style to your file so it sizes and scrolls smoothly inside the Journey.
      </p>
    </div>
  );
};

export default AddHtmlControlPanelContent;
